import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app-shell";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Archive, ArchiveRestore, Upload, ChevronRight } from "lucide-react";
import { StandardJobsSection } from "@/components/standard-jobs/StandardJobsSection";
import { CompanyProfileCard } from "@/components/settings/CompanyProfileCard";
import { ModulesCard } from "@/components/settings/ModulesCard";
import { QuoteTermsCard } from "@/components/settings/QuoteTermsCard";
import { InvoiceSettingsCard } from "@/components/settings/InvoiceSettingsCard";
import { ElectroDocumentationSettingsCard } from "@/components/settings/ElectroDocumentationSettingsCard";
import { useActiveCompany } from "@/hooks/use-active-company";




export const Route = createFileRoute("/_authenticated/settings/")({
  head: () => ({
    meta: [
      { title: "Innstillinger | VERQO" },
      { name: "description", content: "Firmaprofil, priser og innstillinger." },
    ],
  }),
  component: SettingsPage,
});

type Profile = {
  company_name: string | null; org_number: string | null; address: string | null;
  postal_code: string | null; city: string | null; phone: string | null; email: string | null;
};
type Settings = {
  hourly_rate: number; service_vehicle_rate: number; documentation_rate: number;
  lift_rate: number; travel_rate_per_km: number; material_markup_percent: number;
  default_markup_percent: number;
  vat_percent: number; quote_valid_days: number; quote_terms: string | null;
};
type CategoryRow = { id: string; name: string; sort_order: number; is_active: boolean };
type SupplierRow = { id: string; name: string; sort_order: number; is_active: boolean; website: string | null; color: string | null };
type LaborRow = { id: string; name: string; cost_rate: number; sell_rate: number; is_default: boolean; is_active: boolean; sort_order: number };

function SettingsPage() {
  const qc = useQueryClient();
  const { companyId } = useActiveCompany();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [catMarkups, setCatMarkups] = useState<Record<string, number>>({}); // key: category_id


  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const [p, s, u, cm, cats, sups, labs] = await Promise.all([
        supabase.from("profiles").select("*").maybeSingle(),
        supabase.from("settings").select("*").maybeSingle(),
        supabase.auth.getUser(),
        supabase.from("material_category_markups").select("category_id,markup_percent"),
        supabase.from("material_categories").select("id,name,sort_order,is_active").order("sort_order"),
        supabase.from("suppliers").select("id,name,sort_order,is_active,website,color").order("sort_order"),
        supabase.from("labor_rates").select("id,name,cost_rate,sell_rate,is_default,is_active,sort_order").order("sort_order"),
      ]);
      return {
        profile: p.data as Profile | null,
        settings: s.data as Settings | null,
        userId: u.data.user?.id,
        catMarkups: (cm.data ?? []) as { category_id: string; markup_percent: number }[],
        categories: (cats.data ?? []) as CategoryRow[],
        suppliers: (sups.data ?? []) as SupplierRow[],
        laborRates: (labs.data ?? []) as LaborRow[],
      };
    },
  });

  useEffect(() => {
    if (!data) return;
    setProfile(data.profile ?? { company_name: "", org_number: "", address: "", postal_code: "", city: "", phone: "", email: "" });
    setSettings(data.settings ?? {
      hourly_rate: 890, service_vehicle_rate: 750, documentation_rate: 1500,
      lift_rate: 1200, travel_rate_per_km: 8, material_markup_percent: 35,
      default_markup_percent: 35, vat_percent: 25, quote_valid_days: 30, quote_terms: "",
    });
    const initial: Record<string, number> = {};
    for (const c of data.categories) initial[c.id] = 0;
    for (const row of data.catMarkups) initial[row.category_id] = Number(row.markup_percent);
    setCatMarkups(initial);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!profile || !settings || !data?.userId) return;
      const { error: pErr } = await supabase.from("profiles").update(profile).eq("id", data.userId);
      if (pErr) throw pErr;
      const { error: sErr } = await supabase.from("settings").update({
        hourly_rate: Number(settings.hourly_rate),
        service_vehicle_rate: Number(settings.service_vehicle_rate),
        documentation_rate: Number(settings.documentation_rate),
        lift_rate: Number(settings.lift_rate),
        travel_rate_per_km: Number(settings.travel_rate_per_km),
        material_markup_percent: Number(settings.material_markup_percent),
        default_markup_percent: Number(settings.default_markup_percent),
        vat_percent: Number(settings.vat_percent),
        quote_valid_days: Number(settings.quote_valid_days),
        quote_terms: settings.quote_terms,
      }).eq("user_id", data.userId);
      if (sErr) throw sErr;

      const rows = Object.entries(catMarkups)
        .filter(([, v]) => Number(v) > 0)
        .map(([category_id, markup_percent]) => ({
          user_id: data.userId!, company_id: companyId, category_id, markup_percent: Number(markup_percent),
        }));

      if (rows.length > 0) {
        const { error: cErr } = await supabase
          .from("material_category_markups")
          .upsert(rows, { onConflict: "user_id,category_id" });
        if (cErr) throw cErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["category-markups"] });
      toast.success("Innstillinger lagret");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!profile || !settings || !data) return <div className="py-20 text-center text-sm text-muted-foreground">Laster…</div>;

  const setP = (k: keyof Profile) => (e: React.ChangeEvent<HTMLInputElement>) => setProfile({ ...profile, [k]: e.target.value });
  const setS = (k: keyof Settings) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSettings({ ...settings, [k]: e.target.value as unknown as Settings[typeof k] });

  return (
    <>
      <PageHeader title="Innstillinger" subtitle="Firmainformasjon, satser, kategorier, leverandører"
        action={<button onClick={() => save.mutate()} disabled={save.isPending} className="btn btn-primary"><Save className="h-4 w-4" /> Lagre</button>}
      />

      <Link
        to="/settings/import"
        className="mb-6 flex items-center justify-between gap-3 rounded-lg border p-4 hover:border-primary transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2"><Upload className="h-5 w-5 text-primary" /></div>
          <div>
            <div className="font-semibold">Dataimport og migrering</div>
            <div className="text-xs text-muted-foreground">Flytt kunder, prosjekter og materialregister fra andre systemer inn i VERQO.</div>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </Link>

      <div className="grid gap-6 lg:grid-cols-2">
        <CompanyProfileCard userId={data.userId!} />


        <section className="card p-5">
          <h3 className="mb-4 font-semibold">Standardsatser</h3>
          <div className="space-y-3">
            <Field label="Servicebil (kr)" type="number" value={String(settings.service_vehicle_rate)} onChange={setS("service_vehicle_rate")} />
            <Field label="Dokumentasjon (kr)" type="number" value={String(settings.documentation_rate)} onChange={setS("documentation_rate")} />
            <Field label="Lift (kr/dag)" type="number" value={String(settings.lift_rate)} onChange={setS("lift_rate")} />
            <Field label="Reise (kr/km)" type="number" value={String(settings.travel_rate_per_km)} onChange={setS("travel_rate_per_km")} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="MVA %" type="number" value={String(settings.vat_percent)} onChange={setS("vat_percent")} />
              <Field label="Gyldig dager" type="number" value={String(settings.quote_valid_days)} onChange={setS("quote_valid_days")} />
            </div>
            <div>
              <label className="label-base">Standard vilkår på tilbud</label>
              <textarea className="input-base" rows={3} value={settings.quote_terms ?? ""} onChange={setS("quote_terms")} />
            </div>
          </div>
        </section>

        <LaborRatesCard userId={data.userId!} rates={data.laborRates} onChanged={() => qc.invalidateQueries({ queryKey: ["settings"] })} />

        <section className="card p-5">
          <div className="mb-3">
            <h3 className="font-semibold">Påslag</h3>
            <p className="text-xs text-muted-foreground">Produktnivå overstyrer kategori. Kategori overstyrer globalt påslag.</p>
          </div>
          <Field
            label="Globalt påslag %"
            type="number"
            value={String(settings.default_markup_percent)}
            onChange={setS("default_markup_percent")}
          />
          <div className="mt-4 rounded-md border border-border">
            <div className="grid grid-cols-2 border-b border-border bg-muted/40 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
              <div>Kategori</div>
              <div className="text-right">Påslag %</div>
            </div>
            {data.categories.filter((c) => c.is_active).map((c) => (
              <div key={c.id} className="grid grid-cols-2 items-center border-b border-border/60 px-3 py-2 last:border-0">
                <div className="text-sm">{c.name}</div>
                <input
                  type="number" step="0.1"
                  className="input-base ml-auto max-w-[8rem] text-right"
                  value={catMarkups[c.id] ?? 0}
                  onChange={(e) => setCatMarkups((m) => ({ ...m, [c.id]: Number(e.target.value) }))}
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Sett 0 for å bruke globalt påslag.</p>
        </section>

        <CategoriesCard userId={data.userId!} categories={data.categories} onChanged={() => qc.invalidateQueries({ queryKey: ["settings"] })} />
        <SuppliersCard userId={data.userId!} suppliers={data.suppliers} onChanged={() => qc.invalidateQueries({ queryKey: ["settings"] })} />
        <QuoteTermsCard />
        <InvoiceSettingsCard />
        <ModulesCard />
        <ElectroDocumentationSettingsCard />
        <StandardJobsSection userId={data.userId!} />
      </div>
    </>
  );
}

function CategoriesCard({ userId, categories, onChanged }: { userId: string; categories: CategoryRow[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const { companyId } = useActiveCompany();
  const { activeModuleKey: moduleKey } = useActiveCompany();
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const nextOrder = (categories.at(-1)?.sort_order ?? 0) + 1;
      const { error } = await supabase.from("material_categories").insert({
        user_id: userId, company_id: companyId, module_key: moduleKey,
        name: trimmed, sort_order: nextOrder,
      });
      if (error) throw error;
    },

    onSuccess: () => { setName(""); onChanged(); qc.invalidateQueries({ queryKey: ["material-categories"] }); toast.success("Kategori lagt til"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const arch = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase.from("material_categories").update({ is_active: val }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { onChanged(); qc.invalidateQueries({ queryKey: ["material-categories"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <section className="card p-5">
      <h3 className="mb-3 font-semibold">Kategorier</h3>
      <div className="mb-3 flex gap-2">
        <input className="input-base flex-1" placeholder="Ny kategori (f.eks. Solcelle)" value={name} onChange={(e) => setName(e.target.value)} />
        <button onClick={() => add.mutate()} className="btn btn-primary"><Plus className="h-4 w-4" />Legg til</button>
      </div>
      <div className="divide-y divide-border rounded-md border border-border">
        {categories.map((c) => (
          <div key={c.id} className={`flex items-center justify-between px-3 py-2 text-sm ${c.is_active ? "" : "opacity-50"}`}>
            <span>{c.name}{!c.is_active && " (arkivert)"}</span>
            <button
              onClick={() => arch.mutate({ id: c.id, val: !c.is_active })}
              className="btn btn-ghost p-1.5"
              title={c.is_active ? "Arkiver" : "Aktiver"}
            >
              {c.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
            </button>
          </div>
        ))}
        {categories.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Ingen kategorier.</div>}
      </div>
    </section>
  );
}

function SuppliersCard({ userId, suppliers, onChanged }: { userId: string; suppliers: SupplierRow[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const { companyId } = useActiveCompany();
  const { activeModuleKey: moduleKey } = useActiveCompany();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [color, setColor] = useState("#0072CE");
  const add = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const nextOrder = (suppliers.at(-1)?.sort_order ?? 0) + 1;
      const { error } = await supabase.from("suppliers").insert({
        user_id: userId, company_id: companyId, module_key: moduleKey,
        name: trimmed, sort_order: nextOrder,
        website: website.trim() || null, color: color || null,
      });
      if (error) throw error;
    },

    onSuccess: () => { setName(""); setWebsite(""); onChanged(); qc.invalidateQueries({ queryKey: ["suppliers"] }); toast.success("Leverandør lagt til"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const arch = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase.from("suppliers").update({ is_active: val }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { onChanged(); qc.invalidateQueries({ queryKey: ["suppliers"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <section className="card p-5">
      <h3 className="mb-3 font-semibold">Leverandører</h3>
      <div className="mb-3 grid grid-cols-[1fr,1fr,3rem,auto] gap-2">
        <input className="input-base" placeholder="Navn" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="input-base" placeholder="nettside.no" value={website} onChange={(e) => setWebsite(e.target.value)} />
        <input type="color" className="input-base h-10 p-1" value={color} onChange={(e) => setColor(e.target.value)} />
        <button onClick={() => add.mutate()} className="btn btn-primary"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="divide-y divide-border rounded-md border border-border">
        {suppliers.map((s) => (
          <div key={s.id} className={`flex items-center justify-between px-3 py-2 text-sm ${s.is_active ? "" : "opacity-50"}`}>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full border border-border" style={{ background: s.color ?? "transparent" }} />
              <span>{s.name}</span>
              {s.website && <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">{s.website}</a>}
              {!s.is_active && <span className="chip bg-muted text-muted-foreground">Arkivert</span>}
            </div>
            <button
              onClick={() => arch.mutate({ id: s.id, val: !s.is_active })}
              className="btn btn-ghost p-1.5"
              title={s.is_active ? "Arkiver" : "Aktiver"}
            >
              {s.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
            </button>
          </div>
        ))}
        {suppliers.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Ingen leverandører.</div>}
      </div>
    </section>
  );
}

function LaborRatesCard({ userId, rates, onChanged }: { userId: string; rates: LaborRow[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const { companyId } = useActiveCompany();
  const { activeModuleKey: moduleKey } = useActiveCompany();
  const [local, setLocal] = useState<LaborRow[]>(rates);
  const [name, setName] = useState("");
  const [costRate, setCostRate] = useState(420);
  const [sellRate, setSellRate] = useState(890);
  useEffect(() => setLocal(rates), [rates]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["labor-rates"] }); onChanged(); };

  const add = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const nextOrder = (rates.at(-1)?.sort_order ?? 0) + 1;
      const { error } = await supabase.from("labor_rates").insert({
        user_id: userId, company_id: companyId, module_key: moduleKey,
        name: trimmed,
        cost_rate: Number(costRate), sell_rate: Number(sellRate),
        is_default: false, sort_order: nextOrder,
      });
      if (error) throw error;
    },

    onSuccess: () => { setName(""); invalidate(); toast.success("Timekategori lagt til"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const update = useMutation({
    mutationFn: async (r: LaborRow) => {
      const { error } = await supabase.from("labor_rates").update({
        name: r.name,
        cost_rate: Number(r.cost_rate),
        sell_rate: Number(r.sell_rate),
        is_default: r.is_default,
      }).eq("id", r.id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Oppdatert"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const arch = useMutation({
    mutationFn: async ({ id, val }: { id: string; val: boolean }) => {
      const { error } = await supabase.from("labor_rates").update({ is_active: val }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="card p-5">
      <h3 className="mb-3 font-semibold">Timekategorier</h3>
      <p className="mb-3 text-xs text-muted-foreground">Kostpris brukes til å beregne dekningsgrad på arbeid. Salgspris er det kunden faktureres.</p>
      <div className="mb-3 grid grid-cols-[1fr,6rem,6rem,auto] gap-2">
        <input className="input-base" placeholder="Navn" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="number" className="input-base text-right" placeholder="Kost" value={costRate} onChange={(e) => setCostRate(Number(e.target.value))} />
        <input type="number" className="input-base text-right" placeholder="Salg" value={sellRate} onChange={(e) => setSellRate(Number(e.target.value))} />
        <button onClick={() => add.mutate()} className="btn btn-primary"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="divide-y divide-border rounded-md border border-border">
        <div className="grid grid-cols-[1fr,6rem,6rem,4rem,auto] gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground">
          <div>Navn</div><div className="text-right">Kost</div><div className="text-right">Salg</div><div>Std.</div><div />
        </div>
        {local.map((r, i) => (
          <div key={r.id} className={`grid grid-cols-[1fr,6rem,6rem,4rem,auto] items-center gap-2 px-3 py-2 text-sm ${r.is_active ? "" : "opacity-50"}`}>
            <input className="input-base" value={r.name} onChange={(e) => setLocal((l) => l.map((x, k) => k === i ? { ...x, name: e.target.value } : x))} />
            <input type="number" className="input-base text-right" value={r.cost_rate} onChange={(e) => setLocal((l) => l.map((x, k) => k === i ? { ...x, cost_rate: Number(e.target.value) } : x))} />
            <input type="number" className="input-base text-right" value={r.sell_rate} onChange={(e) => setLocal((l) => l.map((x, k) => k === i ? { ...x, sell_rate: Number(e.target.value) } : x))} />
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={r.is_default} onChange={(e) => setLocal((l) => l.map((x, k) => k === i ? { ...x, is_default: e.target.checked } : x))} />
            </label>
            <div className="flex gap-1">
              <button onClick={() => update.mutate(r)} className="btn btn-ghost p-1.5"><Save className="h-4 w-4" /></button>
              <button onClick={() => arch.mutate({ id: r.id, val: !r.is_active })} className="btn btn-ghost p-1.5" title={r.is_active ? "Arkiver" : "Aktiver"}>
                {r.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
              </button>
            </div>
          </div>
        ))}
        {local.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Ingen timekategorier.</div>}
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange }: { label: string; type?: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <div>
      <label className="label-base">{label}</label>
      <input className="input-base" type={type} value={value} onChange={onChange} />
    </div>
  );
}
